import React, { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  buildWellnessTileDrawerState,
  getWellnessTileDrawerDepth,
  isWellnessTileDrawerLocation,
  normalizeWellnessTileTarget,
} from '../helpers/wellnessTileNavigation'
import { WellnessTileNavigationContext } from './wellnessTileNavigationContextValue'

export function WellnessTileNavigationProvider({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const drawerState = location?.state?.wellnessTileDrawer || null
  const isTileDrawerOpen = isWellnessTileDrawerLocation(location)

  const getTileDrawerLinkState = useCallback(
    (target) => buildWellnessTileDrawerState(target, location),
    [location],
  )

  const openTileDrawer = useCallback(
    (target) => {
      const tileTarget = normalizeWellnessTileTarget(target)
      if (!tileTarget) return
      navigate(tileTarget.path, {
        state: buildWellnessTileDrawerState(tileTarget, location),
      })
    },
    [location, navigate],
  )

  const closeTileDrawer = useCallback(() => {
    navigate(-getWellnessTileDrawerDepth(location))
  }, [location, navigate])

  const backFromTileDrawer = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const openTileAsPage = useCallback(() => {
    const tileTarget = normalizeWellnessTileTarget(drawerState)
    if (!tileTarget) return
    navigate(tileTarget.path, { replace: true, state: null })
  }, [drawerState, navigate])

  const value = useMemo(
    () => ({
      isTileDrawerOpen,
      drawerState,
      getTileDrawerLinkState,
      openTileDrawer,
      closeTileDrawer,
      backFromTileDrawer,
      openTileAsPage,
    }),
    [
      backFromTileDrawer,
      closeTileDrawer,
      drawerState,
      getTileDrawerLinkState,
      isTileDrawerOpen,
      openTileAsPage,
      openTileDrawer,
    ],
  )

  return React.createElement(WellnessTileNavigationContext.Provider, { value }, children)
}
